import { isLocalStorageAvailable } from '../util/localStorage';
import { trackEvent } from '../util/tracking';

const LS_KEYS = {
  userLocation: 'location-user',
  preferredUserLocation: 'location-user-preferred',
  lastLocationFetch: 'location-last-fetch',
};

const detectedLocationCookieName = 'local_dc';

const modal = document.querySelector('#location-switch-modal');
const currentCountryCode = modal
  .getAttribute('data-country-code')
  .toLowerCase();
const currentCountryName = modal.getAttribute('data-country-name');
const worldCountryCode = modal
  .getAttribute('data-world-country-code')
  .toLowerCase();

const url = new URL(window.location.href);
const isBlogPostPage =
  url.pathname.includes('/blog/') && !url.pathname.endsWith('/blog/');
const isNewsPage =
  url.pathname.includes('/news/') || url.pathname.includes('/nieuws/');

export function initializeLocationSwitchModal({ onHide } = {}) {
  // Don't show a modal or anyathing if we can't even save where the user
  // is actually from
  if (!isLocalStorageAvailable) {
    removeUrlSearchParam('pc');
    return false;
  }

  // Get preferred location from url, and remove parameter from url
  const urlParams = new URLSearchParams(window.location.search);
  let preferredCountryCode;
  if (urlParams.has('pc')) {
    preferredCountryCode = urlParams.get('pc').toLowerCase();
    storePreferredLocation(preferredCountryCode);
    removeUrlSearchParam('pc');
  } else {
    preferredCountryCode = localStorage.getItem(LS_KEYS.preferredUserLocation);
  }

  // Get detected country code from cookie
  const detectedLocationCookie = window.cookies.getCookie(
    detectedLocationCookieName
  );
  let detectedCountryCode = detectedLocationCookie
    ? detectedLocationCookie.toLocaleLowerCase()
    : null;
  if (!detectedCountryCode) {
    console.error(
      `Didn't find a location detection cookie (${detectedLocationCookieName}), not showing location modal`
    );
    return false;
  }
  // If the user is in a unsupported country, act as if he/she is visiting
  // from 'international'
  if (!Object.keys(window.cece.countryMap).includes(detectedCountryCode)) {
    detectedCountryCode = 'world';
  }

  // If the visitor's detected country code is the same as
  // the current one, don't proceed
  if (detectedCountryCode && detectedCountryCode === currentCountryCode) {
    return false;
  }

  // Stop when we know the visitor is already in the preferred right place
  if (preferredCountryCode && preferredCountryCode === currentCountryCode) {
    return false;
  }

  if (preferredCountryCode) {
    // TODO: potentially we could redirect the visitor straight to the
    // site he/she gave up as preferred location, or we could show a modal
    // saying something like: 'last time you choose to browse the UK website,
    // would you like to go there again?'
    // setCookie('local', preferredCountryCode, 30);
    // window.location.reload()
  }

  // Don't show location switch modal when visitor is on blog post page or news page
  // on the international website
  if (
    isBlogPostPage &&
    (currentCountryCode === worldCountryCode || currentCountryCode === 'uk')
  ) {
    return false;
  }
  if (isNewsPage) {
    return false;
  }

  showModal({
    onHide,
    detectedCountryCode,
  });

  // We're showing the modal, let the initiaer know
  return true;
}

function removeUrlSearchParam(param) {
  const urlRewrite = new URL(window.location.href);
  urlRewrite.searchParams.delete(param);
  history.replaceState(null, '', urlRewrite.toString());
}

function storePreferredLocation(countryCode) {
  const code = countryCode.toLowerCase();
  window.cookies.setCookie('local', code, 30);
  localStorage.setItem(LS_KEYS.preferredUserLocation, code);
}

// TODO: this shouldn't be hardcoded, fine for now as there aren't so many countires
// with 'the' in front of their name
function addThe(name, code) {
  return `${
    code.toLowerCase() === 'gb' || code.toLowerCase() === 'nl' ? 'the' : ''
  } ${name}`;
}

function showModal({ onHide, detectedCountryCode }) {
  if (!detectedCountryCode) {
    console.error(
      `Missing detectedCountryCode properties while initializing location switch modal, detectedCountryCode: ${detectedCountryCode}`
    );
  }

  // Interaction to expand the country flags section of the modal
  const countryFlags = modal.querySelector('.country-flags');
  const expandLink = modal.querySelector('.expand-link a');
  expandLink.addEventListener('click', e => {
    e.preventDefault();
    countryFlags.classList.toggle('expand');
  });

  if (!window.cece.countryMap[detectedCountryCode]) {
    console.error(
      `No country found in countryMap with country code: ${detectedCountryCode}`
    );
  }

  // Set text and button translation text
  modal.querySelector('.trans-text').textContent =
    detectedCountryCode === 'world'
      ? window.i18n.locationSwitchModal.worldText.replace(
          new RegExp('%CURRENT_COUNTRY%', 'g'),
          addThe(currentCountryName, currentCountryCode)
        )
      : window.i18n.locationSwitchModal.text.replace(
          new RegExp('%COUNTRY%', 'g'),
          addThe(
            window.cece.countryMap[detectedCountryCode].name,
            detectedCountryCode
          )
        );
  modal.querySelector('.trans-button').textContent =
    window.i18n.locationSwitchModal.button.replace(
      new RegExp('%COUNTRY%', 'g'),
      window.cece.countryMap[detectedCountryCode].name
    );

  // Bind interaction to go-to-suggestion button
  const button = modal.querySelector('.go-to-suggestion-link');
  button.setAttribute('href', window.cece.countryMap[detectedCountryCode].url);
  button.addEventListener('click', e => {
    e.preventDefault();
    storePreferredLocation(detectedCountryCode);
    window.location.href = window.cece.countryMap[detectedCountryCode].url;
  });

  const $modal = new window.bootstrap.Modal(
    document.getElementById('location-switch-modal')
  );

  // Bind interaction to stay-on-site link
  const stayOnSiteLink = modal.querySelector('.stay-on-site-link');
  const stayUrl = new URL(window.location.href);
  stayUrl.searchParams.set('pc', currentCountryCode);
  stayOnSiteLink.setAttribute('href', stayUrl.toString());
  stayOnSiteLink.addEventListener('click', e => {
    e.preventDefault();
    storePreferredLocation(currentCountryCode);
    $modal.hide();
    if (onHide) {
      onHide();
    }
  });

  // Create country icon in button
  var img = document.createElement('img');
  img.width = 18;
  img.className = 'me-2';
  img.src = window.cece.countryMap[detectedCountryCode].icon;
  modal.querySelector('.trans-button').prepend(img);

  // Show modal
  $modal.show();

  trackEvent(
    'LocationSwitchModal',
    'Show',
    detectedCountryCode,
    currentCountryCode
  );
}
